import type {
  DecidedSpan,
  OverridesSummary,
  PiiType,
  RedactionResult,
} from "./types";
import { placeholderFor } from "./ruleset/placeholders";

/**
 * Applies the user's per-span decisions to produce the final, one-way-redacted
 * prompt plus a PII-free overrides summary (LAR-141, plan decisions 2 & 8).
 *
 * `redact` decisions replace the span with a typed placeholder; `keep` (override)
 * decisions leave the original text in place and are counted in the summary.
 * Overlapping decided spans are defended against (first-wins) but should not
 * occur — `detect()` already resolves overlaps and manual spans are merged by
 * the caller.
 */
export function redact(text: string, decided: DecidedSpan[]): RedactionResult {
  const sorted = [...decided].sort((a, b) => a.start - b.start);

  let out = "";
  let cursor = 0;
  const keptTypes = new Set<PiiType>();
  let keptCount = 0;
  let hardOverridden = false;

  for (const span of sorted) {
    if (span.start < cursor) continue; // skip overlaps defensively
    out += text.slice(cursor, span.start);

    if (span.decision === "redact") {
      // Numbered placeholder + any grammatical suffix ("Peters" → "[NAVN1]s").
      out += placeholderFor(span.type, span.entityId, span.label);
      if (span.inflection) out += span.inflection;
    } else {
      out += text.slice(span.start, span.end); // keep = override
      keptCount += 1;
      keptTypes.add(span.type);
      if (span.tier === "hard") hardOverridden = true;
    }
    cursor = span.end;
  }
  out += text.slice(cursor);

  const overrides: OverridesSummary = {
    count: keptCount,
    types: [...keptTypes],
    hardOverridden,
  };
  return { text: out, overrides };
}

/** Convenience: redact every detected span (the default "accept all" action). */
export function redactAll(
  text: string,
  spans: readonly Omit<DecidedSpan, "decision">[]
): RedactionResult {
  return redact(
    text,
    spans.map((s) => ({ ...s, decision: "redact" as const }))
  );
}
